import { Texture } from 'three';

import { TileKey } from './ortho-tile-typings';

type PendingEntry = {
  key: TileKey;
  priority: number;
  factory: (signal: AbortSignal) => Promise<Texture>;
  resolve: (tex: Texture) => void;
  reject: (err: unknown) => void;
  controller: AbortController;
};

/**
 * TileCache: textures + priority queue + concurrency cap + eviction policy
 *
 * Requests are not fired immediately — they enter a priority queue and are
 * dispatched up to maxConcurrent at a time. This ensures the browser's
 * connection slots are used for the highest-priority (most visible) tiles
 * rather than filling up with stale requests from a previous zoom level.
 */
export class TileCache {
  private readonly maxTextures: number;
  private readonly maxConcurrent: number;

  // ready textures
  private readonly textures = new Map<TileKey, Texture>();

  // shared promise for every key that is pending or inflight (dedup handle)
  private readonly inProgress = new Map<TileKey, Promise<Texture>>();

  // pending queue (sorted highest-priority first)
  private readonly pending: PendingEntry[] = [];

  // abort controllers for requests that are currently executing
  private readonly inflight = new Map<TileKey, AbortController>();

  // naive LRU bookkeeping
  private readonly lru = new Map<TileKey, number>();
  private tick = 0;
  private disposed = false;

  constructor(params: { maxTextures: number; maxConcurrent?: number }) {
    this.maxTextures = params.maxTextures;
    this.maxConcurrent = params.maxConcurrent ?? 6;
  }

  /**
   * Get or create a texture for the given key.
   * - Already ready: returns immediately.
   * - Already pending/inflight: upgrades priority if higher, returns shared promise.
   * - Otherwise: enqueues at given priority.
   */
  getOrCreateTexture(
    key: TileKey,
    factory: (signal: AbortSignal) => Promise<Texture>,
    priority: number,
  ): Promise<Texture> {
    this.touch(key);

    const ready = this.textures.get(key);
    if (ready) {
      return Promise.resolve(ready);
    }

    // Already pending or inflight — reuse the shared promise, upgrade priority if needed
    const existing = this.inProgress.get(key);
    if (existing) {
      this.setPriority(key, priority);
      return existing;
    }

    const p = new Promise<Texture>((resolve, reject) => {
      const controller = new AbortController();
      const entry: PendingEntry = { key, priority, factory, resolve, reject, controller };
      this.pending.push(entry);
      this.sortPending();
      this.dispatch();
    });

    this.inProgress.set(key, p);
    return p;
  }

  /**
   * Update the priority of a pending (not yet dispatched) request.
   * No-op if the request is already inflight or not found.
   */
  setPriority(key: TileKey, priority: number): void {
    const entry = this.pending.find((e) => e.key === key);
    if (entry && priority !== entry.priority) {
      entry.priority = priority;
      this.sortPending();
    }
  }

  /**
   * Cancel a specific tile's pending or inflight request.
   * No-op if the tile is already ready or not known.
   * A cancelled tile is retryable — callers must NOT add it to failedTiles.
   */
  cancel(key: TileKey): void {
    const pendingIdx = this.pending.findIndex((e) => e.key === key);
    if (pendingIdx !== -1) {
      const [entry] = this.pending.splice(pendingIdx, 1);
      this.inProgress.delete(key);
      this.lru.delete(key);
      entry.controller.abort();
      entry.reject(entry.controller.signal.reason);
      return;
    }

    const controller = this.inflight.get(key);
    if (controller) {
      controller.abort();
      // inProgress and lru entries are removed in the dispatch completion handler
    }
  }

  /**
   * Cancel all pending/inflight requests whose keys are NOT in protectedKeys.
   * Ready textures are never affected.
   */
  cancelOutdated(protectedKeys: ReadonlySet<TileKey>): void {
    for (let i = this.pending.length - 1; i >= 0; i--) {
      const entry = this.pending[i];
      if (!protectedKeys.has(entry.key)) {
        this.pending.splice(i, 1);
        this.inProgress.delete(entry.key);
        this.lru.delete(entry.key);
        entry.controller.abort();
        entry.reject(entry.controller.signal.reason);
      }
    }

    for (const [key, controller] of this.inflight) {
      if (!protectedKeys.has(key)) {
        controller.abort();
      }
    }
  }

  /**
   * Cancel all pending and inflight requests. Call on asset dispose.
   */
  cancelAll(): void {
    for (const entry of this.pending) {
      this.inProgress.delete(entry.key);
      entry.controller.abort();
      entry.reject(entry.controller.signal.reason);
    }
    this.pending.length = 0;

    for (const controller of this.inflight.values()) {
      controller.abort();
    }
  }

  /**
   * Frees the cached textures but the given ones. Requests in progress aren't affected.
   */
  freeTexturesExcept(keepKeys: ReadonlySet<TileKey>): void {
    for (const [key, texture] of this.textures) {
      if (!keepKeys.has(key)) {
        texture.dispose();
        this.textures.delete(key);
        this.lru.delete(key);
      }
    }
  }

  /**
   * Cancel everything and free all cached textures. The cache must not be used afterwards.
   */
  dispose(): void {
    this.disposed = true;
    this.cancelAll();
    for (const texture of this.textures.values()) {
      texture.dispose();
    }
    this.textures.clear();
    this.lru.clear();
  }

  private sortPending(): void {
    this.pending.sort((a, b) => b.priority - a.priority);
  }

  private dispatch(): void {
    while (this.inflight.size < this.maxConcurrent && this.pending.length > 0) {
      const entry = this.pending.shift()!;

      if (this.textures.has(entry.key)) {
        // Already loaded via another code path — resolve the waiting promise
        this.inProgress.delete(entry.key);
        entry.resolve(this.textures.get(entry.key)!);
        continue;
      }

      this.inflight.set(entry.key, entry.controller);

      entry
        .factory(entry.controller.signal)
        .then((tex) => {
          // Finished after dispose(): nobody owns the texture anymore
          if (this.disposed) {
            tex.dispose();
            throw new DOMException('Tile cache disposed', 'AbortError');
          }
          this.textures.set(entry.key, tex);
          this.touch(entry.key);
          this.evictIfNeeded();
          entry.resolve(tex);
        })
        .catch((err) => {
          entry.reject(err);
        })
        .finally(() => {
          this.inflight.delete(entry.key);
          this.inProgress.delete(entry.key);
          // If no texture was stored (aborted or error), clean up the lru entry too
          if (!this.textures.has(entry.key)) {
            this.lru.delete(entry.key);
          }
          this.dispatch();
        });
    }
  }

  touch(key: TileKey): void {
    this.tick++;
    this.lru.set(key, this.tick);
  }

  private evictIfNeeded(): void {
    if (this.textures.size <= this.maxTextures) {
      return;
    }

    const entries = [...this.lru.entries()].sort((a, b) => a[1] - b[1]);
    for (const [key] of entries) {
      if (this.textures.size <= this.maxTextures) {
        break;
      }
      const tex = this.textures.get(key);
      if (!tex) {
        continue;
      }

      tex.dispose();
      this.textures.delete(key);
      this.lru.delete(key);
    }
  }
}
